/* eslint react-hooks/exhaustive-deps: "warn" */

import { ChangeEvent, KeyboardEvent, useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { common } from 'libs/http/api/common/common';
import { SearchedBlog } from 'libs/http/api/common/common.types';
import { Link } from 'libs/router/Link';
import { useRouter } from 'libs/router/useRouter';
import { defaultLocale } from 'libs/locale';

import { useOutsideAlerter } from 'hooks/useOutsideAlerter';
import { useInt } from 'hooks/useInt';

import { Icon } from 'ui/atoms/Icon/Icon';
import { LoaderSpin } from 'ui/atoms/LoaderSpin/LoaderSpin';
import { InputForm } from 'ui/molecules/InputForm/InputForm';

import styles from './ArticleSearch.module.scss';

interface Props {
  className?: string;
  variant?: 'pill';
}

const modifyBlogs = (data: SearchedBlog[]) =>
  data.map((item) => ({
    title: item.title,
    slug: item.slug,
  }));

const renderSearchResult = (blog: SearchedBlog, key: number) => (
  <Link key={key} route={blog.slug!}>
    <div className={styles.result}>{blog.title}</div>
  </Link>
);

export const ArticleSearch = ({ className, variant }: Props) => {
  const { t } = useInt();

  const { query } = useRouter();

  const wrapperRef = useRef<HTMLDivElement>(null);
  const debounce = useRef<NodeJS.Timeout>(undefined);

  const [data, setData] = useState<SearchedBlog[]>([]);
  const [loading, setLoading] = useState(false);
  const [empty, setEmpty] = useState(false);
  const [entry, setEntry] = useState('');
  const [visibleResults, setVisibleResults] = useState(false);

  const router = useRouter();

  useOutsideAlerter(wrapperRef, setVisibleResults);

  const onChangeEntry = ({ target }: ChangeEvent<HTMLInputElement>) => setEntry(target.value);

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();

      if (data.length > 0 && loading === false && data[0].slug) {
        router.push(data[0].slug, { lang: `${query.lang || defaultLocale}` });
      }
    }
  };

  const handleSubmit = () => {
    if (data.length > 0 && !loading && data[0].slug) {
      router.push(data[0].slug, { lang: `${query.lang || defaultLocale}` });
    }
  };

  useEffect(() => {
    return () => {
      if (debounce.current !== undefined) {
        clearTimeout(debounce.current);
      }
    };
  }, []);

  useEffect(() => {
    if (entry.length > 1) {
      setLoading(true);
      setEmpty(false);

      if (debounce.current !== undefined) {
        clearTimeout(debounce.current);
      }

      debounce.current = setTimeout(() => {
        (async () => {
          try {
            const { data: newData = [] } = await common.search({
              'filter[title__icontains]': entry,
              lang: `${query.lang || defaultLocale}`,
            });

            if (newData.length === 0) {
              setEmpty(true);
            }

            setData(modifyBlogs(newData));
          } finally {
            setLoading(false);
          }
        })();
      }, 250);
    }
  }, [entry, query.lang]);

  const resultsDropdown =
    visibleResults && entry.length > 1 ? (
      <div className={data.length === 1 ? styles.resultsWrapperExtraPadding : styles.resultsWrapper}>
        {loading ? (
          <div className={styles.resultsLoading}>
            <LoaderSpin size="small" loading />
          </div>
        ) : (
          <>
            {data.length > 5 && (
              <>
                <div className={styles.resultsNumber}>
                  {t('Results:')} {data.length}
                </div>
                <hr />
              </>
            )}

            {empty && (
              <div className={styles.resultsEmpty}>
                <span>{t('Nothing found')}</span>
              </div>
            )}

            {data.map(renderSearchResult)}
          </>
        )}
      </div>
    ) : null;

  if (variant === 'pill') {
    return (
      <div className={cn(styles.searchWrapper, styles.searchWrapperPill, className)} ref={wrapperRef}>
        <InputForm
          fullWidth
          size="md"
          value={entry}
          icon="search"
          placeholder={t('Search article')}
          buttonLabel={t('Search')}
          onSubmit={() => handleSubmit()}
          onChange={(val) => {
            setEntry(val);
            setVisibleResults(true);
          }}
        />

        <div>{resultsDropdown}</div>
      </div>
    );
  }

  return (
    <div className={cn(styles.searchWrapper, className)} ref={wrapperRef}>
      <form id="form-search" className={styles.searchForm}>
        <div className={styles.searchIconWrapper}>
          <Icon type="search" className={styles.searchIcon} />
        </div>

        <input
          aria-label={t('Search article')}
          className={styles.textBox}
          placeholder={t('Search article')}
          onChange={onChangeEntry}
          onKeyDown={handleKeyDown}
        />
      </form>

      <div>{resultsDropdown}</div>
    </div>
  );
};
